/* eslint-disable no-unused-vars */
import { useRef, useState } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { useIntl } from 'react-intl';
import { Add, DeleteOutline, Edit } from '@mui/icons-material';

import { AgColor } from '../../Styles/AgColor';
import AgIconButton from '../../AgIconButton';
import AgTypography from '../../Styles/AgTypography';

import {
  PROFILE_IMAGE_MAX_SIZE,
  PROFILE_IMAGE_MIN_HEIGHT,
  PROFILE_IMAGE_MIN_WIDTH,
} from '../../../Constants';
import profileImage from '../../../stories/Components/AgAvatar/defaultAvatar.svg';

type AgDefaultProps = {
  // eslint-disable-next-line no-unused-vars
  openAvatar?: () => void;
  isFlagShowContent?: boolean;
  noMaxSize?: boolean;
  noMinResolution?: boolean;
  isLoading?: boolean;
};

type AgUploadAccountPhotoProps =
  | ({
      imageURL?: string | null;
      isFile: true;
      saveProfileImage?: (file?: File) => Promise<void>;
    } & AgDefaultProps)
  | ({
      imageURL?: string;
      isFile?: false | undefined;
      saveProfileImage?: (base64?: string) => Promise<void>;
    } & AgDefaultProps);

const AgUploadAccountPhoto = ({
  imageURL,
  saveProfileImage,
  openAvatar,
  isFlagShowContent,
  isFile,
  noMaxSize,
  noMinResolution,
  isLoading,
}: AgUploadAccountPhotoProps) => {
  const [error, setError] = useState<string>();
  const messages = useIntl().formatMessage;
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const convertBase64 = (file: Blob) => {
    return new Promise((resolve, reject) => {
      const fileReader = new FileReader();
      fileReader.readAsDataURL(file);
  
      fileReader.onload = () => {
        resolve(fileReader.result);
      };
  
      fileReader.onerror = (error) => {
        reject(error);
      };
    });
  };
  
  const validateImageResolution = (file: File): Promise<{ width: number; height: number }> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const url = URL.createObjectURL(file);
  
      img.onload = () => {
        const { width, height } = img;
        URL.revokeObjectURL(url); // Clean up memory after image is loaded
        resolve({ width, height });
      };
  
      img.onerror = (error) => {
        reject(error);
      };
  
      img.src = url;
    });
  };
  
  const handleFileChange = async (event: any) => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
  
      // Validate file size
      if (file?.size > PROFILE_IMAGE_MAX_SIZE) {
        setError(messages({ id: 'STAFF_IMAGE_FILE_SIZE_ERROR_MESSAGE' }));
        return;
      }
      
      // Validate image resolution
      const { width, height } = await validateImageResolution(file);
      if (
        (width < PROFILE_IMAGE_MIN_WIDTH ||
          height < PROFILE_IMAGE_MIN_HEIGHT) &&
        !noMinResolution
      ) {
        setError(messages({ id: 'STAFF_IMAGE_FILE_RESOLUTION_ERROR_MESSAGE' }));
        return;
      }

      // Proceed with converting to Base64 and uploading
      if (!isFile) {
        const base: string = (await convertBase64(file)) as string;
        const base64: string = base.includes('data:image')
          ? base
          : 'data:image/jpeg;base64,' + base;
        if (base && base64) {
          saveProfileImage?.(base64);
          setError('');
        }
      } else if (file) {
        saveProfileImage?.(file);
        setError('');
      }
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleFileDelete = async () => {
    try {
      await saveProfileImage?.(undefined);
    } catch(err) {
    
    }
    setError('');
  };

  return(
    <Box
      sx={{
        width: '100%',
        color: AgColor.grey5
      }}
    >
      <Box>
        <Box
          sx={{
            color: AgColor.grey9,
            fontSize: '12px',
            fontWeight: '600'
          }}
        >
          {messages({ id: 'PHOTO' })}
        </Box>
        <Box
          sx={{
            float: 'right',
            color: AgColor.grey9,
            fontSize: '10px',
            transform: 'translateY(-22px)'
          }}
        >
          {messages({ id: 'OPTIONAL' })}
        </Box>
      </Box>
      <Box
        sx={{
          display: 'flex',
          // alignItems: 'center',
          marginTop: '10px',
        }}
      >
        <Box
          sx={{
            height: '72px',
            width: '72px',
            borderRadius: '8px',
            background: 'none',
            display: 'flex',
            flexShrink: 0,
            flexWrap: 'nowrap',
            justifyContent: 'center',
            alignItems: 'center',
            cursor: 'pointer',
          }}
          onClick={() => openAvatar?.()}
        >
          {isLoading ? (
            <CircularProgress />
          ) : (
            <>
              {isFile ? (
                <img
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  src={imageURL || profileImage}
                  alt=''
                />
              ) : (
                <img
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  src={
                    imageURL
                      ? imageURL.includes('data:image')
                        ? imageURL
                        : 'data:image/jpeg;base64,' + imageURL
                      : profileImage
                  }
                  alt=''
                />
              )}
            </>
          )}
        </Box>
        {isFlagShowContent && (
          <Box>
            <Box
              sx={{
                display: 'flex',
                gap: '15px',
                marginLeft: '10px',
                height: 'max-content',
              }}
            >
              <input
                ref={fileInputRef}
                id='selectImage'
                hidden
                type='file'
                onChange={handleFileChange}
                accept='.jpg, .jpeg, .png'
              />
              <AgIconButton
                label={
                  imageURL
                    ? messages({ id: 'CHANGE' })
                    : messages({ id: 'UPLOAD' })
                }
                mainColor={AgColor.secondaryDark}
                icon={imageURL ? <Edit /> : <Add />}
                onClick={() => {
                  document.getElementById('selectImage')?.click();
                }}
              />
              <AgIconButton
                label={messages({ id: 'REMOVE' })}
                mainColor={AgColor.secondaryDark}
                icon={<DeleteOutline />}
                disabled={!imageURL}
                onClick={() => {
                  handleFileDelete();
                }}
              />
            </Box>
            <Box
              sx={{
                marginLeft: '10px',
                fontSize: '12px',
                marginTop: '15px',
              }}
            >
              {messages(
                { id: 'PHOTO_DETAILS2' },
                {
                  minResolution: noMinResolution
                    ? ''
                    : messages({ id: 'PHOTO_DETAILS_MIN_RESOLUTION' }),
                  maxSize: noMaxSize
                    ? ''
                    : ` ${messages({ id: 'PHOTO_DETAILS_MAX_SIZE' })}`,
                }
              )}
            </Box>
          </Box>
        )}
      </Box>
      {error && (
        <AgTypography
          style={{ color: AgColor.errorMain, marginTop: '10px' }}
          contentStyle={'AP.Paragraph.Small.Regular'}
          message={error}
        />
      )}
    </Box>
  );
};

export default AgUploadAccountPhoto;